import React, { createContext, useEffect, useMemo, useState } from "react";
import { getLocalStorageUserSession } from "../services/secureStorage"; // decrypt
import { deriveGradientsFromBrand } from "../utils/deriveGradients";
import { logger } from "./logger";

type ThemeTokens = {
  gray: string; // fijo
  loader: string; // fijo
  brand: string; // viene del back
  grad1: string; // derivado de brand
  grad2: string; // derivado de brand
  grad3: string; // derivado de brand
  errStart: string; // fijo
  errEnd: string; // fijo
  logoUrl: string | null;
  companyId: string;
  companyName?: string;
  fontColor: string; // viene del back (textColor)
};

const FIXED = {
  gray: "#999999",
  loader: "#21222c99",
  errStart: "#fcdada",
  errEnd: "#d94646",
};

const FALLBACK: ThemeTokens = {
  ...FIXED,
  brand: "#614ac9",
  grad1: "#c8c0ec",
  grad2: "#b3e0ed",
  grad3: "#9effee",
  logoUrl: "/logo-zeus.svg",
  companyId: "",
  companyName: undefined,
  fontColor: "#FFFFFF",
};

export type ColorContextValue = ThemeTokens & { setBrand: (v: string) => void };

export const ColorContext = createContext<ColorContextValue>({
  ...FALLBACK,
  setBrand: () => {},
});
ColorContext.displayName = "ColorContext";

type Props = { children: React.ReactNode };

function applyCssVars(t: ThemeTokens) {
  const r = document.documentElement;
  r.style.setProperty("--gray", t.gray);
  r.style.setProperty("--loader", t.loader);
  r.style.setProperty("--brand", t.brand);
  r.style.setProperty("--grad-color-1", t.grad1);
  r.style.setProperty("--grad-color-2", t.grad2);
  r.style.setProperty("--grad-color-3", t.grad3);
  r.style.setProperty("--grad-error-start", t.errStart);
  r.style.setProperty("--grad-error-end", t.errEnd);
  r.style.setProperty("--font", t.fontColor);
}

export function ColorProvider({ children }: Props) {
  const [theme, setTheme] = useState<ThemeTokens>(FALLBACK);

  useEffect(() => {
    (async () => {
      try {
        const decrypted = await getLocalStorageUserSession<any>(); // { data: {...} } | null
        const d = decrypted?.data;
        const cfg = d?.company?.configuration ?? {};
        const comp = d?.company ?? {};

        // SOLO 2 colores que llegan del back
        const brand = cfg.color || FALLBACK.brand;
        const font = cfg.textColor || cfg.font || FALLBACK.fontColor;

        // Derivamos gradientes desde brand
        const { grad1, grad2, grad3 } = deriveGradientsFromBrand(brand);

        const next: ThemeTokens = {
          gray: FIXED.gray,
          loader: FIXED.loader,
          errStart: FIXED.errStart,
          errEnd: FIXED.errEnd,
          brand,
          grad1,
          grad2,
          grad3,
          fontColor: font,
          logoUrl: comp.photo ?? comp.logo ?? FALLBACK.logoUrl,
          companyId: String(comp.id_company ?? comp.id ?? ""),
          companyName: comp.company_name ?? comp.name ?? undefined,
        };

        applyCssVars(next);
        setTheme(next);
      } catch (e) {
        logger.error("ColorProvider: error leyendo sesión:", e);
        applyCssVars(FALLBACK);
        setTheme(FALLBACK);
      }
    })();
  }, []);

  // Si quieres poder cambiar brand on-the-fly desde UI:
  const setBrand = (v: string) => {
    const { grad1, grad2, grad3 } = deriveGradientsFromBrand(v);
    const next = { ...theme, brand: v, grad1, grad2, grad3 };
    applyCssVars(next);
    setTheme(next);
  };

  const value = useMemo<ColorContextValue>(() => ({ ...theme, setBrand }), [theme]);

  return <ColorContext.Provider value={value}>{children}</ColorContext.Provider>;
}
